import { useCallback } from 'react';

import { parseJson } from 'utils/string';

import type { GTMItemListId, GTMItemListName } from './gtm';

type StorageKey = 'GTM_LIST_DATA';

type StorageValueForType<T> = Omit<Extract<StorageValue, { type: T }>, 'type'>;

// Extract<T>, and us wants a union and this might contain multiple types
// in the future so we need to disable the interface transformation here
// oxlint-disable-next-line typescript/consistent-type-definitions
type StorageValue = {
	type: 'GTM_LIST_DATA';
	gtmItemListId: GTMItemListId;
	gtmItemListName: GTMItemListName;
};

type SessionStorageKey = 'POSTAL_CODE';

// oxlint-disable-next-line typescript/consistent-type-definitions
type SessionStorageValue = {
	type: 'POSTAL_CODE';
	postalCode: string;
};

type SessionStorageValueForType<T> = Omit<
	Extract<SessionStorageValue, { type: T }>,
	'type'
>;

interface UseTemporaryStoredValue<T> {
	getStoredValue: () => StorageValueForType<T> | undefined;
	setStoredValue: (newValue: StorageValueForType<T>) => void;
}

/**
 * Hook to store a value in localStorage temporarily.
 */
export function useTemporaryStoredValue<T extends StorageKey>(
	key: StorageKey,
): UseTemporaryStoredValue<T> {
	const setStoredValue = useCallback(
		(newValue: unknown) => {
			if (typeof localStorage !== 'undefined') {
				localStorage.setItem(key, JSON.stringify(newValue));
			}
		},
		[key],
	);
	const getStoredValue = useCallback((): StorageValueForType<T> | undefined => {
		if (typeof localStorage === 'undefined') return undefined;
		const storedValue = localStorage.getItem(key);
		localStorage.removeItem(key);
		if (!storedValue) return undefined;
		return parseJson<StorageValueForType<T>>(storedValue) ?? undefined;
	}, [key]);

	return {
		setStoredValue,
		getStoredValue,
	};
}

interface UseSessionStorage<T extends SessionStorageKey> {
	getSessionValue: () => SessionStorageValueForType<T> | undefined;
	setSessionValue: (newValue: SessionStorageValueForType<T>) => void;
}

export function useSessionStorage<T extends SessionStorageKey>(
	key: T,
): UseSessionStorage<T> {
	const setSessionValue = useCallback(
		(newValue: SessionStorageValueForType<T>) => {
			if (typeof sessionStorage !== 'undefined') {
				sessionStorage.setItem(key, JSON.stringify(newValue));
			}
		},
		[key],
	);

	const getSessionValue = useCallback(():
		| SessionStorageValueForType<T>
		| undefined => {
		if (typeof sessionStorage === 'undefined') return undefined;
		return (
			parseJson<SessionStorageValueForType<T>>(sessionStorage.getItem(key)) ??
			undefined
		);
	}, [key]);

	return { setSessionValue, getSessionValue };
}
